/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { BillingApiService } from '../../../http';
import { CheckoutService } from '../../../services/component';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LogService, UtilsService } from '../../../../../../core/services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-select-payment-card',
    templateUrl: './select-payment-card.component.html',
    styleUrls: ['./select-payment-card.component.scss']
})
export class SelectPaymentCardComponent extends BaseComponent implements OnInit {
    /**
     * Step index set by parent
     */
    @Input() public stepIndex: number;

    /**
     *
     */
    @Output() public selectedPayment: EventEmitter<any> = new EventEmitter();

    /**
     * Active state from the checkout service.
     */
    public activeStep: number = null;

    /**
     * Store list of payment cards.
     */
    public cards: any = [];

    /**
     * Store the selected product
     */
    public selectedCard: any = {
        id: null,
        index: null
    };

    constructor(
        private billingApiService: BillingApiService,
        private checkoutService: CheckoutService,
        private logService: LogService,
        private utilsService: UtilsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setUpPage();
        this.subscribeToCheckoutService();

        this.activeStep = this.checkoutService.activeStep;
    }

    /**
     * Set the selected product in the form.
     * @param card
     * @param index
     */
    public setSelectedCard(card: any, index: number): void {
        this.checkoutService.selectedCardId = card.id;
        this.selectedCard = this.formatCard(card);
        this.selectedCard.index = index;
    }

    /**
     * Set step in checkout.
     */
    public setStep(): void {
        this.checkoutService.setStep(this.stepIndex);
    }

    /**
     * Set up page
     * @private
     */
    private setUpPage(): void {
        this.billingApiService.getPaymentMethods()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.populateCards(data);
                    if (this.cards.length === 1) {
                        this.setSelectedCard(this.cards[0], 0);
                    } else if (this.checkoutService.selectedCardId) {
                        const selectedCardIndex = this.cards.findIndex((item: any): boolean =>
                            item.id === this.checkoutService.selectedCardId
                        );

                        if (selectedCardIndex !== -1) {
                            this.setSelectedCard(this.cards[selectedCardIndex], selectedCardIndex);
                        }
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SelectProductComponent:setUpPage', error.message);
                }
            });
    }

    /**
     * populate products.
     * @param data
     * @private
     */
    private populateCards(data: any): void {
        const cardArray: any[] = [];
        for (const row of data) {
            const dateObject = new Date(row.expire_date);
            const adjustedDate = new Date(dateObject.setMonth(dateObject.getMonth() + 1));
            if (adjustedDate >= new Date()) {
                cardArray.push(this.formatCard(row));
            }
        }

        this.cards = cardArray;
    }

    /**
     * Format card.
     * @param card
     * @private
     */
    private formatCard(card: any): any {
        // The expiry month is a month out from what is entered on the stripe form.
        const dateObject = new Date(card.expire_date);
        const adjustedDate = new Date(dateObject.setMonth(dateObject.getMonth() + 1));
        const date: string = this.utilsService.createDateTimeString(adjustedDate, false);

        return {
            id: card.id,
            brand: card.brand,
            brandLogoPath: this.getBrandLogo(card.brand),
            expiry: date,
            'expire_date': card.expire_date,
            last4: card.last4
        };
    }

    /**
     * Get logo path.
     * @param brand
     * @private
     */
    private getBrandLogo(brand: string): string {
        const knownBrands: string[] = [
            'amazon',
            'amex',
            'apple_pay',
            'bitcoin',
            'diners',
            'discover',
            'google_pay',
            'jcb',
            'maestro',
            'mastercard',
            'paypal',
            'visa',
        ];

        const name: string = knownBrands.includes(brand) ? brand : 'default_card';

        return `assets/centralisedManagement/icons/billing_${name}.svg`;
    }

    /**
     * Subscribe to check out service.
     * @private
     */
    private subscribeToCheckoutService(): void {
        this.checkoutService.activeStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (stepIndex: any): void => {
                    this.activeStep = stepIndex;
                }
            });
    }
}
